/**
 * Draft-approach proof panel. Renders the committed output of
 * `tools/draft_proof.py` (copied to `frontend/public/draft_proof_summary.json`
 * via `--emit-frontend`). It backs one methodology claim:
 *
 *   If the rankings are accurate, the Monte-Carlo recommender builds better
 *   starting lineups than greedy "take the highest VBD", and far better than
 *   drafting by ADP alone.
 *
 * Every drafter uses the SAME projections, so the charts isolate strategy, not
 * projection quality. Numbers are regenerated by re-running the harness.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ErrorBar,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface CellSummary {
  label: string;
  n: number;
  mc_mean: number;
  greedy_mean: number;
  adp_mean: number;
  mc_vs_greedy: number;
  mc_vs_greedy_ci: number;
  mc_vs_greedy_winpct: number;
  mc_vs_adp: number;
  mc_vs_adp_ci: number;
  mc_vs_adp_winpct: number;
  season?: number;
  teams?: number;
  superflex?: boolean;
}

interface ProofSummary {
  meta: {
    generated: string;
    drafts_per_cell: number;
    nsims: number;
    total_drafts: number;
    grade: string;
    seasons: number[];
    team_sizes: number[];
  };
  overall: CellSummary;
  by_season: CellSummary[];
  by_size: CellSummary[];
  by_format: CellSummary[];
  by_cell: CellSummary[];
}

const marginData = (rows: CellSummary[]) =>
  rows.map((r) => ({
    label: r.label,
    margin: r.mc_vs_greedy,
    ci: r.mc_vs_greedy_ci,
    winpct: r.mc_vs_greedy_winpct,
  }));

const DraftProofView: React.FC = () => {
  const [data, setData] = useState<ProofSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL || ''}/draft_proof_summary.json`;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch(() => setError('proof-summary-missing'));
  }, []);

  if (error) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="semibold">Proof data not generated yet.</Text>
          <Text fontSize="sm">
            Run <code>python tools/draft_proof.py --emit-frontend</code> to build the
            benchmark, then reload. It writes{' '}
            <code>frontend/public/draft_proof_summary.json</code>.
          </Text>
        </Box>
      </Alert>
    );
  }

  if (!data) {
    return <Text color="gray.500">Loading proof…</Text>;
  }

  const ov = data.overall;

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Heading size="md" mb={1}>
          Does simulating the draft actually help?
        </Heading>
        <Text fontSize="sm" color="gray.600">
          Across {data.meta.total_drafts.toLocaleString()} simulated drafts
          (seasons {data.meta.seasons.join('/')}, {data.meta.team_sizes.join('/')}-team,
          1QB &amp; superflex), three strategies drafted from the{' '}
          <b>same projections</b>. Each team is graded purely on its optimal
          starting-lineup points, so this isolates the <i>drafting strategy</i> —
          it is not a claim about any real season result.
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel>MC vs. greedy-VBD</StatLabel>
          <StatNumber color="blue.500">+{ov.mc_vs_greedy} pts/team</StatNumber>
          <StatHelpText>
            MC wins {ov.mc_vs_greedy_winpct}% · 95% CI ±{ov.mc_vs_greedy_ci}
          </StatHelpText>
        </Stat>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel>MC vs. pure-ADP</StatLabel>
          <StatNumber color="orange.500">+{ov.mc_vs_adp} pts/team</StatNumber>
          <StatHelpText>
            MC wins {ov.mc_vs_adp_winpct}% · 95% CI ±{ov.mc_vs_adp_ci}
          </StatHelpText>
        </Stat>
        <Stat borderWidth="1px" borderRadius="md" p={3}>
          <StatLabel>Lineup points / team</StatLabel>
          <StatNumber>{ov.mc_mean}</StatNumber>
          <StatHelpText>
            greedy {ov.greedy_mean} · ADP {ov.adp_mean}
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Box>
          <Heading size="sm" mb={2}>
            MC − greedy-VBD by season
          </Heading>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={marginData(data.by_season)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis label={{ value: 'pts/team', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={(v: any, name: any) =>
                  name === 'margin' ? [`+${v}`, 'MC − greedy'] : [v, name]
                }
              />
              <ReferenceLine y={0} stroke="#444" />
              <Bar dataKey="margin" fill="#2b8cbe" name="margin">
                <ErrorBar dataKey="ci" width={4} strokeWidth={1.5} stroke="#1a1a1a" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Box>
          <Heading size="sm" mb={2}>
            MC − greedy-VBD by league size
          </Heading>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={marginData(data.by_size)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis label={{ value: 'pts/team', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={(v: any, name: any) =>
                  name === 'margin' ? [`+${v}`, 'MC − greedy'] : [v, name]
                }
              />
              <ReferenceLine y={0} stroke="#444" />
              <Bar dataKey="margin" fill="#41ab5d" name="margin">
                <ErrorBar dataKey="ci" width={4} strokeWidth={1.5} stroke="#1a1a1a" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </SimpleGrid>

      <Box>
        <Heading size="sm" mb={2}>
          Optimal starting-lineup points (the ADP blowout)
        </Heading>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={[
              { name: 'MC', pts: ov.mc_mean },
              { name: 'greedy-VBD', pts: ov.greedy_mean },
              { name: 'pure-ADP', pts: ov.adp_mean },
            ]}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 'auto']} label={{ value: 'pts/team', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Bar dataKey="pts">
              <Cell fill="#2b8cbe" />
              <Cell fill="#41ab5d" />
              <Cell fill="#d95f0e" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box>
        <Heading size="sm" mb={2}>
          Every cell (no single season/size hides a regression)
        </Heading>
        <Box overflowX="auto">
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>Season</Th>
                <Th>Size</Th>
                <Th>Format</Th>
                <Th isNumeric>MC − greedy</Th>
                <Th isNumeric>Win %</Th>
                <Th isNumeric>MC − ADP</Th>
                <Th isNumeric>n</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.by_cell.map((c) => (
                <Tr key={c.label}>
                  <Td>{c.season}</Td>
                  <Td>{c.teams}</Td>
                  <Td>{c.superflex ? 'SF' : '1QB'}</Td>
                  <Td isNumeric>
                    <Badge colorScheme={c.mc_vs_greedy >= 0 ? 'green' : 'red'}>
                      {c.mc_vs_greedy >= 0 ? '+' : ''}
                      {c.mc_vs_greedy}
                    </Badge>
                  </Td>
                  <Td isNumeric>{c.mc_vs_greedy_winpct}%</Td>
                  <Td isNumeric>+{c.mc_vs_adp}</Td>
                  <Td isNumeric>{c.n}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      <Text fontSize="xs" color="gray.500">
        Generated {data.meta.generated} · {data.meta.drafts_per_cell} drafts/cell ·
        {data.meta.nsims} MC rollouts/pick · grade: {data.meta.grade}.
      </Text>
    </VStack>
  );
};

export default DraftProofView;
